import { FaLinkedin } from 'react-icons/fa6'
import { FiArrowUpRight } from 'react-icons/fi'
import { Container, Reveal, RevealGroup, HoverText } from '@/components/common'
import type { Consultant } from '@/types/consultant'
import { consultants } from '@/constants/consultant'

const ConsultantRow = ({
  name,
  credentials,
  role,
  image,
  bio,
  highlights,
  publications,
  linkedin,
}: Consultant) => (
  <article className="grid gap-12 border-t border-border py-16 first:border-t-0 first:pt-0 lg:grid-cols-[minmax(0,360px)_1fr] lg:gap-16">
    {/* Left: portrait, identity, highlights */}
    <div>
      <div className="overflow-hidden">
        <img
          src={image}
          alt={`Portrait of ${name}`}
          className="aspect-4/5 w-full object-cover object-top"
          loading="lazy"
        />
      </div>

      <h3 className="mt-6 text-2xl font-bold leading-snug tracking-tight text-ibon-dark">
        {name}, {credentials}
      </h3>
      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-sky">{role}</p>

      <a
        href={linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className="group/link mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark transition-colors hover:text-ibon-sky"
      >
        <FaLinkedin size={16} />
        <HoverText text="Connect on LinkedIn" />
        <FiArrowUpRight
          className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
          size={14}
        />
      </a>

      <div className="mt-10 grid grid-cols-2 gap-6 border-t border-border pt-8">
        {highlights.map((highlight) => (
          <div key={highlight.label}>
            <div className="text-3xl font-bold tracking-tight text-ibon-sky">{highlight.value}</div>
            <p className="mt-1 text-xs leading-snug text-text-muted">{highlight.label}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Right: bio and publications */}
    <div>
      {bio.map((paragraph) => (
        <p key={paragraph} className="mb-5 text-base leading-relaxed text-text-muted last:mb-0">
          {paragraph}
        </p>
      ))}

      <div className="mt-10 border-t border-border pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ibon-sky">
          Selected Publications
        </p>
        <ul className="mt-5 space-y-4">
          {publications.map((publication) => (
            <li key={publication.title} className="flex flex-col gap-1 sm:flex-row sm:gap-6">
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-text-muted sm:w-36">
                {publication.year}
              </span>
              <span className="text-sm leading-relaxed text-ibon-dark">{publication.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </article>
)

export const StrategicConsultant = () => {
  return (
    <section className="bg-surface py-24 lg:py-32">
      <Container>
        <div className="max-w-3xl">
          <Reveal variant="up" delay={100}>
            <h2 className="mt-7 text-4xl font-bold leading-[1.02] tracking-[-0.02em] text-ibon-dark sm:text-5xl lg:text-6xl">
              Strategic guidance, <span className="text-ibon-dark/30">proven globally.</span>
            </h2>
          </Reveal>
          <Reveal variant="up" delay={200}>
            <p className="mt-8 text-lg leading-relaxed text-text-muted">
              IBON World draws on decades of global business, technology, and risk management
              expertise to sharpen our strategy and strengthen the impact of our programmes.
            </p>
          </Reveal>
        </div>

        <RevealGroup className="mt-4" step={100}>
          {consultants.map((person) => (
            <ConsultantRow key={person.name} {...person} />
          ))}
        </RevealGroup>
      </Container>
    </section>
  )
}
